import styled from '@emotion/styled';
import router from 'next/router';
import { SurveyGroup } from '../../apiService';
import { useUser } from '../../CacheProvider';
import { VotesStateType } from './ContentSurvey';
import { __t } from '../../i18n';
import { globalPadding } from '../../Layout/Layout';
import RemainingVotes from './RemainingVotes';
import Select from '../../Select';
import { Flex, Text, Button, Box } from '../../styles';
import { paperBg } from '../../styles/Paper';

const VotesWrapper = styled(Flex)(({ theme }) => ({
  flexShrink: 0,
  [theme.breakpoints.up('md')]: {
    width: `${100 / 3 * 2}%`,
    '& > div': {
      width: '50%',
    },
  },
  [theme.breakpoints.up('lg')]: {
    width: '50%',
  },
}));

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: theme.rgba(theme.palette.background[paperBg.weak][0], .5),
  borderRadius: theme.radius.default,
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    borderRadius: 0,
  },
}));

type SurveyHeaderType = {
  surveyGroup: SurveyGroup,
  activeSurvey: number,
  onChangeSurvey: (index: number) => void,
  onSubmit: () => void,
  remainingPoints: {
    group: number,
  },
  disabled: boolean,
  userParticipatedPoints?: boolean,
  votes: VotesStateType
};

export default function SurveyHeader({
  surveyGroup,
  activeSurvey,
  onChangeSurvey: handleSurveyChange,
  onSubmit,
  remainingPoints,
  disabled,
  userParticipatedPoints,
  votes,
}: SurveyHeaderType) {
  const { title, description, surveys, policy } = surveyGroup;
  const { loaded, authorized } = useUser();
  const surveyTitle = surveys && surveys[activeSurvey].title;
  const totalPoints = votes ? Object.values(votes).map(({ point }) => point).reduce((t, p) => t + p, 0) : 0;

  const handleSubmit = () => {
    if (loaded && !authorized) return router.push(`/login?redirect=${router.asPath}`);
    !disabled && onSubmit();
  };

  const getButtonTitle = (): string => {
    let messageId = 'survey.header.final_save_btn';
    if (loaded && !authorized) messageId = 'survey.header.login_submit';
    if (disabled) messageId = userParticipatedPoints ? 'survey.header.survey_submited_btn' : 'survey.header.survey_ended_btn';
    return __t(messageId);
  };

  const pristine = remainingPoints.group === 0 || totalPoints === 0;
  const buttonDisabled = disabled || (authorized && pristine);

  return (
    <Container p={{ xs: 3, md: 4 }} px={globalPadding}>
      <Text variant="h2" mb={3}>{title}</Text>
      <Flex
        direction={{ xs: 'column', md: 'row' }}
        justify="space-between"
        align={{ md: 'flex-start' }}
      >
        <Text variant="body1" pl={1}>{description}</Text>

        <VotesWrapper
          direction={{ xs: 'column', md: 'row' }}
          justify="flex-end"
          pr={{ md: 1 }}
        >
          {loaded && authorized && surveys && (
            <RemainingVotes
              group={{
                total: policy.max_points,
                count: remainingPoints.group,
              }}
              item={{
                total: disabled ? 0 : surveys[activeSurvey].policy.max_points,
                count: disabled ? 0 : totalPoints,
                title: surveyTitle,
              }}
            />
          )}
          <Box mt={{ xs: 2, md: 0 }} pr={{ md: 1 }}>
            <Select
              mb={2}
              value={activeSurvey}
              options={surveys.map(({ title }, index) => ({ label: title || '', value: index }))}
              onChange={({ target }) => handleSurveyChange(target.value)}
            />
            <Button variant="primaryB" fullWidth onClick={handleSubmit} disabled={buttonDisabled}>
              {getButtonTitle()}
            </Button>
          </Box>
        </VotesWrapper>
      </Flex>
    </Container>
  );
}
